import {
  ORDINAL,
  PERCENT_MEASURES,
  applyInsightTransform,
  bucketKey,
  bucketRange,
  insightMeasure,
  insightSegments,
  insightValues,
  isAdditive,
  round1,
  segmentNamesOf,
  transformIsPercent,
  transformLabel,
  type InsightRow,
} from './insight-rows'
import { PREVIOUS_PERIOD, THIS_PERIOD, previousPeriod, scopedRows } from './insight-scope'
import {
  INSIGHT_SERIES_MAX,
  insightDimensionLabel,
  insightMeasureLabel,
  type InsightBucket,
  type InsightChart,
  type InsightDimension,
  type InsightSegment,
  type InsightSpec,
} from './insight-spec'

export interface InsightPoint {
  label: string
  x: number | null
  y: number | null
  size: number | null
}

export interface InsightResult {
  chart: InsightChart
  /** One entry per axis category, in draw order: `label`, one number per series key, and the
   *  secondary measure over the whole category (null when the spec has none). */
  rows: Array<{ label: string; values: Record<string, number | null>; secondary: number | null }>
  /** The split's values in legend order; `['value']` when there is no split. */
  seriesKeys: string[]
  /** The single figure, for a kpi. */
  value: number | null
  /** Rows the spec's filters kept, out of `scopeCount`. */
  matched: number
  scopeCount: number
  xLabel: string
  measureLabel: string
  /** The primary values are percentages (a percent stack, a share measure or transform). */
  percent: boolean
  secondaryLabel: string | null
  secondaryPercent: boolean
  /** scatter / bubble: one point per category. */
  points: InsightPoint[]
  pointLabels: {
    x: string
    y: string
    size: string | null
    xPercent: boolean
    yPercent: boolean
  } | null
  /** Where the reference line sits, or null. */
  reference: number | null
  referenceLabel: string | null
  /** Small multiples, on shared axes; empty without a facet. */
  facets: Array<{ label: string; result: InsightResult }>
  highlight: string[]
  /** A figure compared with the previous period: its value then, and the change in %. */
  previous: number | null
  change: number | null
  /** What the reader should know about this chart — shown under it, and read by the model. */
  notes: string[]
}

export const OTHER_SERIES = 'Other'

/**
 * A spec over rows → numbers to draw. Pure and deterministic: the console's preview, the saved
 * Analytics block and the numbers the model checks with `aggregate` all come from this one
 * function.
 */
export function computeInsight(scope: InsightRow[], spec: InsightSpec): InsightResult {
  const rows = scopedRows(scope, spec)
  const previous = previousPeriod(scope, spec)
  if (!previous) return computeInsightWithoutCompare(scope, spec, rows)
  const notes = previous.note ? [previous.note] : []
  if (spec.chart === 'kpi' || (spec.x === null && spec.segments.length === 0)) {
    const now = computeCore(rows, spec, { scopeCount: scope.length, dateRange: null })
    const then = insightMeasure(previous.rows, spec.measure)
    const change =
      now.value !== null && then !== null && then !== 0
        ? round1(((now.value - then) / Math.abs(then)) * 100)
        : null
    return { ...now, previous: then, change, notes: [...now.notes, ...notes] }
  }
  if (spec.x !== 'date' && previous.rows.length + rows.length > 0) {
    // Two series over the same categories: which period a row belongs to IS its series.
    const current = new Set(rows)
    const both = computeCore([...rows, ...previous.rows], spec, {
      scopeCount: scope.length,
      dateRange: null,
      periodOf: (row) => (current.has(row) ? THIS_PERIOD : PREVIOUS_PERIOD),
    })
    return { ...both, matched: rows.length, notes: [...both.notes, ...notes] }
  }
  if (spec.x === 'date') {
    notes.push(
      'Comparing periods works for figures and categories, not a time axis — it is already one.',
    )
  }
  const plain = computeInsightWithoutCompare(scope, spec, rows)
  return { ...plain, notes: [...plain.notes, ...notes] }
}

/** A dimension's values across rows: in its own order when it has one, most frequent first
 *  otherwise. */
function rankedValues(rows: InsightRow[], field: InsightDimension, bucket: InsightBucket) {
  const counts = new Map<string, number>()
  for (const row of rows) {
    for (const value of insightValues(row, field, bucket)) {
      counts.set(value, (counts.get(value) ?? 0) + 1)
    }
  }
  const ordinal = ORDINAL[field]
  return [...counts.entries()]
    .sort((a, b) => (ordinal ? ordinal.indexOf(a[0]) - ordinal.indexOf(b[0]) : b[1] - a[1]))
    .map(([value]) => value)
}

function computeInsightWithoutCompare(
  scope: InsightRow[],
  spec: InsightSpec,
  rows: InsightRow[],
): InsightResult {
  // Shared axes for small multiples: every panel draws the same date range.
  const dateKeys = rows.map((row) => bucketKey(row.publishedAt, spec.bucket)).sort()
  const dateRange: [string, string] | null =
    dateKeys.length > 0 ? [dateKeys[0]!, dateKeys.at(-1)!] : null

  const whole = computeCore(rows, spec, { scopeCount: scope.length, dateRange })
  if (!spec.facet || spec.chart === 'kpi') return whole

  const facet = spec.facet
  const panels = rankedValues(rows, facet, spec.bucket).slice(0, spec.facetLimit)
  return {
    ...whole,
    facets: panels.map((label) => {
      const subset = rows.filter((row) => insightValues(row, facet, spec.bucket).includes(label))
      return { label, result: computeCore(subset, spec, { scopeCount: subset.length, dateRange }) }
    }),
  }
}

interface CoreOptions {
  scopeCount: number
  /** The date axis's first and last bucket — shared, so small multiples line up. */
  dateRange: [string, string] | null
  /** Comparing periods: the series a row belongs to, in place of the spec's own split. */
  periodOf?: ((row: InsightRow) => string) | undefined
}

/** The categories a spec draws along its axis, and the rows in each. */
interface Axis {
  segments: InsightSegment[]
  /** No `x`: the segments are the categories. */
  segmentsAsCategories: boolean
  byCategory: Map<string, InsightRow[]>
  ordinal: string[] | undefined
}

/** How the rows of a category divide into series. `of` null is no split: one series, `value`. */
interface Split {
  keys: string[]
  of: ((row: InsightRow) => string[]) | null
}

function computeCore(rows: InsightRow[], spec: InsightSpec, options: CoreOptions): InsightResult {
  const base = baseResult(rows, spec, options.scopeCount)
  const segments = insightSegments(spec)
  const segmentsAsCategories = spec.x === null && segments.length > 0
  if (spec.chart === 'kpi' || (spec.x === null && !segmentsAsCategories)) {
    return {
      ...base,
      chart: 'kpi',
      value: insightMeasure(rows, spec.measure),
      percent: PERCENT_MEASURES.has(spec.measure),
      measureLabel: insightMeasureLabel(spec.measure),
    }
  }

  const axis: Axis = {
    segments,
    segmentsAsCategories,
    byCategory: groupByCategory(rows, spec, segments, segmentsAsCategories),
    ordinal: spec.x ? ORDINAL[spec.x] : undefined,
  }
  const sorted = axisLabels(axis, spec, options.dateRange)
  const xLabel = segmentsAsCategories ? 'Segment' : insightDimensionLabel(spec.x!)

  if (spec.chart === 'scatter' || spec.chart === 'bubble') {
    return { ...base, xLabel, ...pointsOf(axis, spec, sorted) }
  }

  const labels = limitLabels(axis, spec, customOrder(sorted, spec))
  const split = splitOf(rows, spec, axis, options.periodOf)
  const columns = cellColumns(labels, axis.byCategory, split, spec)
  const secondary = secondaryColumn(labels, axis.byCategory, spec)
  const stackPercent =
    spec.percent &&
    isAdditive(spec.measure) &&
    split.keys.length > 1 &&
    !transformIsPercent(spec.transform)
  const out = drawnRows({ labels, columns, secondary, keys: split.keys, stackPercent })
  const reference = referenceLevel(out, split.keys, spec.reference)

  return {
    ...base,
    rows: out,
    seriesKeys: split.keys,
    xLabel,
    percent: stackPercent || base.percent,
    ...secondaryLabels(spec),
    reference,
    referenceLabel: reference === null ? null : spec.reference,
  }
}

function baseResult(rows: InsightRow[], spec: InsightSpec, scopeCount: number): InsightResult {
  return {
    chart: spec.chart,
    rows: [],
    seriesKeys: ['value'],
    value: null,
    matched: rows.length,
    scopeCount,
    xLabel: '',
    measureLabel: transformLabel(insightMeasureLabel(spec.measure), spec.transform, spec.window),
    percent: PERCENT_MEASURES.has(spec.measure) || transformIsPercent(spec.transform),
    secondaryLabel: null,
    secondaryPercent: false,
    points: [],
    pointLabels: null,
    reference: null,
    referenceLabel: null,
    facets: [],
    highlight: spec.highlight,
    previous: null,
    change: null,
    notes: [],
  }
}

/** A part-of-a-whole chart — no split, and what the limit cut becomes "Other". */
const isWhole = (spec: InsightSpec) => spec.chart === 'donut' || spec.chart === 'treemap'

/** An ordinal axis keeps its own order (and every bucket) unless sorted by value. */
const keepsOrdinalOrder = (ordinal: string[] | undefined, spec: InsightSpec): ordinal is string[] =>
  ordinal !== undefined && (spec.sort === null || spec.sort === 'label')

function groupByCategory(
  rows: InsightRow[],
  spec: InsightSpec,
  segments: InsightSegment[],
  segmentsAsCategories: boolean,
): Map<string, InsightRow[]> {
  const categoriesOf = (row: InsightRow): string[] =>
    segmentsAsCategories ? segmentNamesOf(segments, row) : insightValues(row, spec.x!, spec.bucket)
  const byCategory = new Map<string, InsightRow[]>()
  for (const row of rows) {
    for (const category of categoriesOf(row)) {
      const list = byCategory.get(category)
      if (list) list.push(row)
      else byCategory.set(category, [row])
    }
  }
  return byCategory
}

/** The categories along the axis, in draw order. */
function axisLabels(axis: Axis, spec: InsightSpec, dateRange: [string, string] | null): string[] {
  if (spec.x === 'date') {
    return dateRange ? bucketRange(dateRange[0], dateRange[1], spec.bucket) : []
  }
  if (keepsOrdinalOrder(axis.ordinal, spec)) return [...axis.ordinal]
  if (axis.segmentsAsCategories && spec.sort === null) {
    return axis.segments.map((segment) => segment.name)
  }
  const totals = new Map(
    [...axis.byCategory].map(([label, list]) => [
      label,
      insightMeasure(list, spec.measure) ?? -Infinity,
    ]),
  )
  const labels = [...axis.byCategory.keys()]
  if (spec.sort === 'label') labels.sort((a, b) => a.localeCompare(b))
  else {
    const direction = spec.sort === 'valueAsc' ? 1 : -1
    labels.sort((a, b) => direction * ((totals.get(a) ?? 0) - (totals.get(b) ?? 0)))
  }
  return labels
}

/** scatter / bubble: a point per category. */
function pointsOf(
  axis: Axis,
  spec: InsightSpec,
  labels: string[],
): Pick<InsightResult, 'points' | 'pointLabels'> {
  const { byCategory } = axis
  const axes = spec.points ?? { x: 'count', y: 'netSentiment', size: 'reach' }
  const size = spec.chart === 'bubble' ? (axes.size ?? 'reach') : null
  const ranked =
    spec.x === 'date' || axis.ordinal
      ? labels
      : [...byCategory.keys()]
          .sort((a, b) => byCategory.get(b)!.length - byCategory.get(a)!.length)
          .slice(0, spec.limit)
  return {
    points: ranked
      .filter((label) => byCategory.has(label))
      .map((label) => {
        const list = byCategory.get(label)!
        return {
          label,
          x: insightMeasure(list, axes.x),
          y: insightMeasure(list, axes.y),
          size: size ? insightMeasure(list, size) : null,
        }
      }),
    pointLabels: {
      x: insightMeasureLabel(axes.x),
      y: insightMeasureLabel(axes.y),
      size: size ? insightMeasureLabel(size) : null,
      xPercent: PERCENT_MEASURES.has(axes.x),
      yPercent: PERCENT_MEASURES.has(axes.y),
    },
  }
}

/** The spec's own category order first; anything unlisted follows in the order it had. */
function customOrder(labels: string[], spec: InsightSpec): string[] {
  if (spec.order.length === 0 || spec.x === 'date') return labels
  const rank = new Map(spec.order.map((label, index) => [label.toLowerCase(), index]))
  const at = (label: string) => rank.get(label.toLowerCase()) ?? spec.order.length
  return labels
    .map((label, index) => ({ label, index }))
    .sort((a, b) => at(a.label) - at(b.label) || a.index - b.index)
    .map(({ label }) => label)
}

/** The first `limit` categories of a non-date, non-ordinal axis. */
function limitLabels(axis: Axis, spec: InsightSpec, labels: string[]): string[] {
  if (spec.x === 'date' || keepsOrdinalOrder(axis.ordinal, spec)) return labels
  const rest = labels.slice(spec.limit)
  const kept = labels.slice(0, spec.limit)
  // A part-of-a-whole chart keeps what the limit cut as a slice of its own, when it adds up.
  if (isWhole(spec) && isAdditive(spec.measure) && rest.length > 0) {
    axis.byCategory.set(OTHER_SERIES, [
      ...new Set(rest.flatMap((label) => axis.byCategory.get(label)!)),
    ])
    kept.push(OTHER_SERIES)
  }
  return kept
}

/** The split: the period, segments, or a second dimension's biggest values plus "Other". */
function splitOf(
  rows: InsightRow[],
  spec: InsightSpec,
  axis: Axis,
  periodOf: CoreOptions['periodOf'],
): Split {
  if (isWhole(spec)) return { keys: ['value'], of: null }
  if (periodOf) return { keys: [THIS_PERIOD, PREVIOUS_PERIOD], of: (row) => [periodOf(row)] }
  if (!axis.segmentsAsCategories && axis.segments.length > 0) {
    return {
      keys: axis.segments.map((segment) => segment.name),
      of: (row) => segmentNamesOf(axis.segments, row),
    }
  }
  if (spec.series) return dimensionSplit(rows, spec.series, spec.bucket)
  return { keys: ['value'], of: null }
}

function dimensionSplit(rows: InsightRow[], series: InsightDimension, bucket: InsightBucket) {
  const ranked = rankedValues(rows, series, bucket)
  // An ordinal split is bounded and reads in order, so it keeps every bucket: cutting hours at
  // eight would fold 08h–23h into "Other" and a weekday × hour heatmap would lose the day.
  const kept = new Set(ORDINAL[series] ? ranked : ranked.slice(0, INSIGHT_SERIES_MAX))
  return {
    keys: [...kept, ...(ranked.length > kept.size ? [OTHER_SERIES] : [])],
    of: (row: InsightRow) => [
      ...new Set(
        insightValues(row, series, bucket).map((value) => (kept.has(value) ? value : OTHER_SERIES)),
      ),
    ],
  }
}

/** A category's rows by the series each falls in. */
function cellsOf(list: InsightRow[], split: Split): Map<string, InsightRow[]> {
  const cells = new Map<string, InsightRow[]>()
  for (const row of list) {
    for (const key of split.of ? split.of(row) : ['value']) {
      const cell = cells.get(key)
      if (cell) cell.push(row)
      else cells.set(key, [row])
    }
  }
  return cells
}

/** Each series' measure per category, transformed along the axis. */
function cellColumns(
  labels: string[],
  byCategory: Map<string, InsightRow[]>,
  split: Split,
  spec: InsightSpec,
): Map<string, (number | null)[]> {
  const empty = isAdditive(spec.measure) ? 0 : null
  const columns = new Map<string, (number | null)[]>(split.keys.map((key) => [key, []]))
  for (const label of labels) {
    const cells = cellsOf(byCategory.get(label) ?? [], split)
    for (const key of split.keys) {
      const cell = cells.get(key)
      columns.get(key)!.push(cell ? insightMeasure(cell, spec.measure) : empty)
    }
  }
  for (const key of split.keys) {
    columns.set(key, applyInsightTransform(columns.get(key)!, spec.transform, spec.window))
  }
  return columns
}

/** The secondary measure over each whole category, transformed along the axis. */
function secondaryColumn(
  labels: string[],
  byCategory: Map<string, InsightRow[]>,
  spec: InsightSpec,
): (number | null)[] {
  if (!spec.secondary) return labels.map(() => null)
  const secondary = spec.secondary
  const secondaryEmpty = isAdditive(secondary.measure) ? 0 : null
  return applyInsightTransform(
    labels.map((label) => {
      const list = byCategory.get(label)
      return list ? insightMeasure(list, secondary.measure) : secondaryEmpty
    }),
    secondary.transform,
    spec.window,
  )
}

function drawnRows({
  labels,
  columns,
  secondary,
  keys,
  stackPercent,
}: {
  labels: string[]
  columns: Map<string, (number | null)[]>
  secondary: (number | null)[]
  keys: string[]
  /** Each category as 100% of its series. */
  stackPercent: boolean
}): InsightResult['rows'] {
  return labels.map((label, index) => {
    const values: Record<string, number | null> = {}
    for (const key of keys) values[key] = columns.get(key)![index] ?? null
    if (stackPercent) {
      const total = Object.values(values).reduce<number>((sum, value) => sum + (value ?? 0), 0)
      for (const key of keys) {
        values[key] = total === 0 ? 0 : round1(((values[key] ?? 0) / total) * 100)
      }
    }
    return { label, values, secondary: secondary[index] ?? null }
  })
}

/** The reference line, over what is drawn (one series only — several have no one level). */
function referenceLevel(
  out: InsightResult['rows'],
  keys: string[],
  reference: InsightSpec['reference'],
): number | null {
  if (reference === 'none' || keys.length !== 1) return null
  const drawn = out
    .map((row) => row.values[keys[0]!])
    .filter((value): value is number => value !== null && value !== undefined)
    .sort((a, b) => a - b)
  if (drawn.length === 0) return null
  if (reference === 'average') return round1(drawn.reduce((a, b) => a + b, 0) / drawn.length)
  return drawn.length % 2
    ? drawn[Math.floor(drawn.length / 2)]!
    : round1((drawn[drawn.length / 2 - 1]! + drawn[drawn.length / 2]!) / 2)
}

function secondaryLabels(
  spec: InsightSpec,
): Pick<InsightResult, 'secondaryLabel' | 'secondaryPercent'> {
  if (!spec.secondary) return { secondaryLabel: null, secondaryPercent: false }
  return {
    secondaryLabel: transformLabel(
      insightMeasureLabel(spec.secondary.measure),
      spec.secondary.transform,
      spec.window,
    ),
    secondaryPercent:
      PERCENT_MEASURES.has(spec.secondary.measure) || transformIsPercent(spec.secondary.transform),
  }
}
